import {
    ClaimApi,
    ClaimTransmissionStatusType,
    ClaimWorkflowHistoryItem,
    CoreFetch,
    GetAdjustmentRemarkReportRequest,
    GetAdjustmentReportRequest,
    ProviderAdjustmentRemarkReportItem,
    PaymentApi,
    ProviderAdjustmentReportItem,
    ProviderClaim,
    ProviderClaimDetails,
    ProviderClaimPayment,
    ProviderTransmissionStatusReportItem,
    ReportApi,
} from '@enter/core';
import { fetch } from './util';

/** How many payment ROWS one drill-down reads before it stops and says so. */
export const PAYMENT_ROW_CAP = 5000;

export class DenialsService {
    /**
     * The adjustment report — every CARC the practice was hit with over a date range.
     *
     * This is the only endpoint that can currently say anything about denials. It takes `{start,
     * end}` and nothing else, so payer and provider scope are not expressible. It returns
     * `{group, reasonCode, count, amount}`, and neither number is what its name suggests: `count` is
     * remits and `amount` averages per remit (measured on production 2026-09-16). See `denial-rows.ts`
     * in core for the evidence and what the table does with it.
     */
    public fetchAdjustmentReport = async (
        request: GetAdjustmentReportRequest,
    ): Promise<ProviderAdjustmentReportItem[] | undefined> => {
        return fetch(request, async req => CoreFetch<ReportApi>(ReportApi).getAdjustmentReport(req));
    };

    /**
     * The adjustment-remark report — service-line adjustments grouped by adjustment group,
     * adjustment code, AND the distinct remark-code set on the line.
     *
     * This is what the reworked page reads instead of `fetchAdjustmentReport`, and it answers what
     * that one could not: the remark half of the code, and a usable amount.
     *
     * Three things measured on production 2026-09-30 that the request shape does not show:
     *
     * - it costs about **11.7 s** for a 7-day window (189 rows, 36 KB), against 1.6 s for the
     *   equivalent payment query, so it is a page load and not something to re-run on every filter
     *   change;
     * - `start` is inclusive and `end` is EXCLUSIVE — build the window with `denialsWeekWindow`
     *   rather than by hand, or a week silently becomes six days;
     * - it returns EVERY adjustment, contractual write-offs included, not only denials.
     */
    public fetchAdjustmentRemarkReport = async (
        request: GetAdjustmentRemarkReportRequest,
    ): Promise<ProviderAdjustmentRemarkReportItem[] | undefined> => {
        return fetch(request, async req =>
            CoreFetch<ReportApi>(ReportApi).getAdjustmentRemarkReport(req),
        );
    };

    /**
     * The transmission-status report, filtered to rejections.
     *
     * An AGGREGATE — one row per distinct message, with a count and a charge — not a list of claims.
     * The per-claim source cannot back a worklist: measured on production 2026-09-30,
     * `/provider/status` ignores `filterCreated` completely, has no status-type filter, and costs
     * 2000 records / 3.7 MB / 36 s to surface about seventy rejections. See `rejection-rows.ts`.
     *
     * `REJECTED` is the platform's own definition and is broader than the X12 rejection categories:
     * it counted 136 over a week where an A3+A7 reading of the same window counted 34. We use it for
     * consistency with the worker rejection configuration page and say so on screen (FE-116).
     *
     * Start is inclusive, end is EXCLUSIVE, like every other report here.
     */
    public fetchRejectionReport = async (
        start: string,
        end: string,
        type: ClaimTransmissionStatusType = ClaimTransmissionStatusType.REJECTED,
    ): Promise<ProviderTransmissionStatusReportItem[] | undefined> => {
        return fetch({ start, end }, async req =>
            CoreFetch<ReportApi>(ReportApi).getTransmissionStatusReport({
                ...req,
                transmissionStatusType: type,
            }),
        );
    };

    /**
     * Every payment in a window that carries a given adjustment reason code on any line.
     *
     * ## Not filtered to DENIED, and that is the whole point
     *
     * The first version asked for `filterStatus=EQ:DENIED`, on the reasoning that a denials table's
     * drill-down shows denied payments. It found **nothing** for a row with 198 adjustments. Measured
     * on production 2026-10-01:
     *
     *     OA:23  + status=DENIED   ->   0 payments
     *     OA:23  reason only       -> 100 payments, 100/100 carry 23 on a line,
     *                                 PROCESSED_AS_SECONDARY 26 · REVERSAL_OF_PREVIOUS_PAYMENT 74
     *     CO:B13 reason only       -> 100 payments, 100/100 carry B13, all PROCESSED_AS_PRIMARY
     *
     * A denial ADJUSTMENT is not a DENIED PAYMENT. A line can be denied while its payment as a whole
     * is processed, and the table counts adjustments by reason code across every payment status. So
     * the drill-down scopes by the code instead.
     *
     * ## Reason only, not reason + group
     *
     * Adding `filterAdjustmentGroup` returned the same set but took 8.0 s against 1.4 s. The group
     * still matters — PR:23 is not OA:23 — but `selectPaymentsForRow` already enforces it at the line
     * item, so the server only has to narrow and the client keeps it exact.
     *
     * `BT` on `filterPaymentDate` is read as inclusive at both ends, so the caller passes the window's
     * inclusive LAST day rather than the exclusive `end` the reports take.
     */
    public fetchPaymentsForCode = async (
        reasonCode: string,
        firstDay: string,
        lastDay: string,
    ): Promise<{ payments: ProviderClaimPayment[]; truncated: boolean }> => {
        const all: ProviderClaimPayment[] = [];
        // A safety cap, and SAID when hit: the caller shows that the list stops short rather than
        // presenting a cut-off set as the whole week (Copilot, PR #581). Rows, not payments — the list
        // repeats a payment once per matching line, so the cap reaches fewer distinct payments.
        for (let offset = 0; offset < PAYMENT_ROW_CAP; offset += 100) {
            const page = await fetch({ offset }, async req =>
                CoreFetch<PaymentApi>(PaymentApi).listProviderClaimPayments({
                    filterAdjustmentReason: [`EQ:${reasonCode}`],
                    filterPaymentDate: [`BT:${firstDay}:${lastDay}`],
                    limit: 100,
                    offset: req.offset,
                }),
            );
            const rows = page || [];
            all.push(...rows);
            if (rows.length < 100) {
                return { payments: all, truncated: false };
            }
        }

        return { payments: all, truncated: true };
    };

    /**
     * The claim a payment settles.
     *
     * Through the LIST endpoint, because a payment carries no claim id: its `professionalId` is the
     * professional-claim sub-record's id, for which every by-id claim endpoint answers 500 (measured on
     * production 2026-10-01). Undefined when the payment resolves to no claim — the Phase 0 batches
     * came back one or two short, so that case is real.
     */
    public fetchClaimForPayment = async (paymentId: string): Promise<ProviderClaim | undefined> => {
        const claims = await fetch({ paymentId }, async req =>
            CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({
                filterPaymentId: [`EQ:${req.paymentId}`],
                limit: 5,
            }),
        );

        return (claims || [])[0];
    };

    /** Codings, professionals, payments and the rest. 127–622 KB and 1.4–5.7 s on the claims sampled. */
    public fetchClaimDetails = async (claimId: string): Promise<ProviderClaimDetails | undefined> =>
        fetch({ id: claimId }, async req => CoreFetch<ClaimApi>(ClaimApi).getProviderClaimDetails(req));

    public fetchClaimHistory = async (claimId: string): Promise<ClaimWorkflowHistoryItem[] | undefined> =>
        fetch({ id: claimId }, async req => CoreFetch<ClaimApi>(ClaimApi).getClaimWorkflowHistoryProvider(req));
}
